import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import { Button, Input, Tooltip } from 'antd';
import type { InputRef } from 'antd';
import { useTranslation } from 'react-i18next';
import CustomTag from '@components/CustomTag';
import CustomModalInfo from '@components/CustomModalInfo';
import { PlusCircleOutlined } from '@ant-design/icons';
import SessionContext from '@providers/Session/SessionContext';

import * as S from './styles';

interface ColumnEmailsProps {
  emails: string[];
  onChange: (emails: string[]) => void;
}

const ColumnEmails = ({ emails, onChange }: ColumnEmailsProps) => {
  const { t } = useTranslation('alerts');
  const { theme } = useContext(SessionContext);

  const inputRef = useRef<InputRef>(null);
  const hasFiveEmails = emails?.length === 5;

  const [value, setValue] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [openNewEmail, setOpenNewEmail] = useState(false);

  useEffect(() => {
    if (openNewEmail) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 300);
    }
  }, [openNewEmail]);

  const toggleOpenNewEmail = () => setOpenNewEmail(crr => !crr);

  const handleAdd = () => {
    if (!value) return;

    if (emails?.includes(value)) {
      setErrorMessage(t('consumption.emailHasAlreadyBeenAdded'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value as string)) {
      setErrorMessage(t('consumption.invalidEmail'));
      return;
    }
    if (emails) {
      onChange([...emails, value]);
    } else {
      onChange([value]);
    }
    toggleOpenNewEmail();
    setValue(undefined);
  };

  const handleRemove = (removedEmail: string) =>
    onChange(emails?.filter(email => email !== removedEmail));

  return (
    <S.ColumnEmailsContainer>
      <Tooltip
        title={t(
          `consumption.${
            hasFiveEmails ? 'youCanOnlyAddUpToEmails' : 'addEmail'
          }`,
          {
            count: 5,
          },
        )}
      >
        <Button
          size="small"
          icon={<PlusCircleOutlined />}
          onClick={toggleOpenNewEmail}
          disabled={hasFiveEmails}
        />
      </Tooltip>

      {emails?.map(email => (
        <CustomTag
          key={email}
          closable={emails?.length > 1}
          onClose={() => handleRemove(email)}
          color="blue"
        >
          {email}
        </CustomTag>
      ))}

      <CustomModalInfo
        title={t('consumption.newEmail')}
        open={openNewEmail}
        onClose={() => {
          toggleOpenNewEmail();
          setValue(undefined);
          setErrorMessage(undefined);
        }}
        footer={
          <Button type="primary" onClick={handleAdd}>
            {t('consumption.confirm')}
          </Button>
        }
      >
        <Input
          ref={inputRef}
          value={value}
          onChange={e => {
            if (errorMessage) {
              setErrorMessage(undefined);
            }
            setValue(e.target.value);
          }}
          onPressEnter={handleAdd}
        />
        {errorMessage && (
          <span
            style={{
              display: 'block',
              marginTop: 4,
              color: theme?.colors?.red,
            }}
          >
            {`* ${errorMessage}`}
          </span>
        )}
      </CustomModalInfo>
    </S.ColumnEmailsContainer>
  );
};

export default memo(ColumnEmails);
